
/**
 * Dashboard Real-Time Service — native FastAPI WebSocket
 *
 * Single persistent connection to /ws (realtime_v3.py WebSocketManager).
 * All events arrive as JSON envelopes:
 *   { type: string, channel: string, data: any, timestamp: string }
 *
 * Channel routing:
 *   "dashboard"   — price:tick, scan:update, cycle:state, cycle:update,
 *                   session:update, pattern:update, money_flow:update,
 *                   kpi:update, setup:alert
 *   "autotrader"  — mt5:state, trade:event, autotrader:log
 *   "trade-state" — trade_state:update, lineage:update
 *   "system"      — connected (welcome message)
 *
 * Drop-in API: dashboardSocket.on / dashboardSocket.emit
 * and autoTraderSocket.on / autoTraderSocket.emit are preserved so that
 * AutoTrader.tsx, CommandCenter.tsx, Matrix.tsx, and store/index.tsx
 * can use native WebSocket transport with the same event semantics.
 */

import authService from './auth';

// ── Types ───────────────────────────────────────────────────────────────────

export interface MT5State {
  timestamp: string;
  connected: boolean;
  ea_connected: boolean;
  account: Record<string, any>;
  positions: any[];
  orders?: any[];
  account_id?: string;
  /** 'ctrader_openapi' on cTrader pushes (api/ctrader_state_publisher.py); absent on MT5 EA pushes. */
  source?: string;
  /** cTrader: the account or positions read was a last-good snapshot, not a fresh one. */
  stale?: boolean;
  positions_fresh?: boolean;
}

export interface PriceTick {
  symbol: string;
  bid: number;
  ask: number;
  time: string;
  /** Tick-bus source ('ctrader_openapi' | 'oanda' | …) when WS_PRICE_SOURCE=tickbus. */
  source?: string;
}

export interface TradeEvent {
  timestamp: string;
  cmd_id: string;
  cmd: string;
  result: Record<string, any>;
}

export interface AutoTraderLogEntry {
  timestamp: string;
  level: string;
  message: string;
}

export interface ScanUpdate {
  timestamp: string;
  summary: Record<string, any>;
  top_setups: any[];
}

export interface SetupAlert {
  timestamp: string;
  count: number;
  setups: any[];
}

export interface CycleState {
  timestamp: string;
  symbol: string;
  state: Record<string, any>;
}

// ── Internal envelope shape from realtime_v3.py ─────────────────────────────

interface WsEnvelope {
  type: string;
  channel: string;
  data: any;
  timestamp: string;
}

// ── Reconnecting WebSocket connection ───────────────────────────────────────

type EventHandler = (data: any) => void;

const CAPACITOR_API_BASE = 'https://bocapital-dashboard-312264840660.europe-west1.run.app';

function buildWsUrl(): string {
  if (window.location.protocol === 'capacitor:') {
    return CAPACITOR_API_BASE.replace(/^https/, 'wss').replace(/^http/, 'ws') + '/ws';
  }
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/ws`;
}

class ReconnectingWebSocket {
  private ws: WebSocket | null = null;
  private _connected = false;
  private _destroyed = false;
  private _reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private _reconnectDelay = 1000;
  private readonly _maxDelay = 30000;
  private readonly _handlers = new Map<string, Set<EventHandler>>();

  constructor() {
    this._connect();
    // A fresh login provides the token the handshake needs — reconnect at once
    // instead of waiting out the (possibly 30s) backoff from pre-login 1008s.
    if (typeof window !== 'undefined') {
      window.addEventListener('bocapital:login', () => this._reconnectNow());
    }
  }

  /** Tear down the current socket and reconnect immediately (e.g. after login). */
  private _reconnectNow(): void {
    if (this._destroyed) return;
    if (this._reconnectTimer !== null) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }
    this._reconnectDelay = 1000;
    try {
      if (this.ws) {
        // Drop handlers so the imminent close doesn't schedule a second reconnect.
        this.ws.onclose = null;
        this.ws.onerror = null;
        this.ws.onmessage = null;
        this.ws.close();
      }
    } catch { /* ignore */ }
    this.ws = null;
    this._connect();
  }

  private _connect(): void {
    if (this._destroyed) return;

    try {
      // Browsers can't set an Authorization header on a WebSocket, so the
      // session token rides in the subprotocol list (read server-side in
      // realtime_v3._resolve_ws_user).  This replaces the bocapital_session
      // cookie, which is not reliably presented on the /ws handshake after the
      // move to app.bocapital.co.uk (host-scoped cookie + SameSite=strict).
      const token = authService.getToken();
      this.ws = token
        ? new WebSocket(buildWsUrl(), ['bocapital-bearer', token])
        : new WebSocket(buildWsUrl());
    } catch (e) {
      this._scheduleReconnect();
      return;
    }

    this.ws.onopen = () => {
      this._connected = true;
      this._reconnectDelay = 1000;
      this._dispatch('ws:connect', {});
      // Request a price snapshot immediately so the UI has data
      this._send({ type: 'request_snapshot' });
    };

    this.ws.onclose = () => {
      this._connected = false;
      this._dispatch('ws:disconnect', {});
      this._scheduleReconnect();
    };

    this.ws.onerror = () => {
      // onclose fires right after onerror — no separate handling needed
    };

    this.ws.onmessage = (ev: MessageEvent) => {
      let envelope: WsEnvelope;
      try {
        envelope = JSON.parse(ev.data as string);
      } catch {
        return;
      }
      // price:batch fan-out: the server coalesces ticks into one frame to
      // cut WS rate; here we replay each tick to existing 'price:tick'
      // subscribers so consumers stay unchanged.  We also still dispatch
      // the raw 'price:batch' so a future subscriber can opt in to batches.
      if (envelope.type === 'price:batch' && envelope.data && Array.isArray(envelope.data.ticks)) {
        for (const tick of envelope.data.ticks as PriceTick[]) {
          this._dispatch('price:tick', tick);
        }
      }
      // Dispatch to global type listeners (e.g. 'price:tick')
      this._dispatch(envelope.type, envelope.data);
      // Also dispatch a channel-scoped event so channel managers can filter
      this._dispatch(`${envelope.channel}:*`, envelope);
    };
  }

  private _scheduleReconnect(): void {
    if (this._destroyed) return;
    this._reconnectTimer = setTimeout(() => {
      this._reconnectDelay = Math.min(this._reconnectDelay * 2, this._maxDelay);
      this._connect();
    }, this._reconnectDelay);
  }

  private _dispatch(event: string, data: any): void {
    const handlers = this._handlers.get(event);
    if (!handlers) return;
    handlers.forEach(h => {
      try { h(data); } catch { /* never let a handler crash the connection */ }
    });
  }

  _send(payload: object): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(payload));
    }
  }

  on(event: string, handler: EventHandler): () => void {
    if (!this._handlers.has(event)) {
      this._handlers.set(event, new Set());
    }
    this._handlers.get(event)!.add(handler);
    return () => { this._handlers.get(event)?.delete(handler); };
  }

  get isConnected(): boolean { return this._connected; }

  destroy(): void {
    this._destroyed = true;
    if (this._reconnectTimer !== null) clearTimeout(this._reconnectTimer);
    this.ws?.close();
  }
}

// Single shared connection for the whole app
const _conn = new ReconnectingWebSocket();

// ── Channel-filtered view ───────────────────────────────────────────────────
// Provides the same .on() / .emit() / .connect() / .disconnect() interface as
// the old Socket.IO managers so callers need zero changes.

class ChannelSocketManager {
  private _channel: string;
  private _active = false;

  constructor(channel: string) {
    this._channel = channel;
  }

  get isConnected(): boolean { return _conn.isConnected; }

  connect(): void { this._active = true; }
  disconnect(): void { this._active = false; }

  /**
   * Subscribe to an event type.
   * handler receives the `data` payload from the envelope (same shape as
   * the old Socket.IO event argument).
   * Returns an unsubscribe function — call it from useEffect cleanup.
   */
  on(event: string, handler: EventHandler): () => void {
    // Special connect/disconnect lifecycle events
    if (event === 'connect') return _conn.on('ws:connect', handler);
    if (event === 'disconnect') return _conn.on('ws:disconnect', handler);
    // Filter by channel envelope first, then match event type.
    return _conn.on(`${this._channel}:*`, (envelope: WsEnvelope) => {
      if (!this._active) return;
      if (!envelope || envelope.type !== event) return;
      handler(envelope.data);
    });
  }

  /** Send a client→server message (e.g. request_scan, request_snapshot). */
  emit(event: string, data?: any): void {
    _conn._send({ type: event, ...(data ? { data } : {}) });
  }

  requestScan(): void { this.emit('request_scan'); }
  requestMoneyFlow(period = '1D'): void { this.emit('request_money_flow', { period }); }
  requestStatus(): void { this.emit('request_status'); }
}

// ── Singletons (same names as before) ───────────────────────────────────────

export const dashboardSocket = new ChannelSocketManager('dashboard');
export const autoTraderSocket = new ChannelSocketManager('autotrader');
